import { useFetch } from '@/composables/useFetch';
import { ref, watch, type Ref } from 'vue';

// ── Module-level image cache ────────────────────────────────────────
interface CacheEntry {
  data: string;
  timestamp: number;
}

const imageCache = new Map<string, CacheEntry>();
const DEFAULT_TTL_MS = 15 * 60 * 1000; // 15 minutes

function getCached(url: string): string | null {
  const entry = imageCache.get(url);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > DEFAULT_TTL_MS) {
    imageCache.delete(url);
    return null;
  }
  return entry.data;
}

function setCache(url: string, data: string): void {
  imageCache.set(url, { data, timestamp: Date.now() });
}

/** Clear all cached images (e.g. on logout). */
export function clearImageCache(): void {
  imageCache.clear();
}
// ────────────────────────────────────────────────────────────────────

/**
 * Fetches a base64 image from an API endpoint URL.
 * Results are cached in memory for 15 minutes to avoid redundant requests.
 * Returns a reactive ref with the base64 data URI, suitable for use as img :src.
 */
export function useImageBase64(url: Ref<string | null | undefined>) {
  const { fetchData } = useFetch();
  const imageSrc = ref<string>('');
  const isLoading = ref(false);
  const isError = ref(false);

  async function fetchImage(bypassCache = false) {
    const imgUrl = url.value;
    if (!imgUrl) {
      imageSrc.value = '';
      return;
    }

    // Serve from cache if available and fresh
    if (!bypassCache) {
      const cached = getCached(imgUrl);
      if (cached) {
        imageSrc.value = cached;
        return;
      }
    }

    isLoading.value = true;
    isError.value = false;

    const { data, error } = await fetchData(imgUrl);

    isLoading.value = false;

    if (error) {
      console.error('[useImageBase64] Error fetching image:', error);
      isError.value = true;
      return;
    }

    if (data?.image) {
      imageSrc.value = data.image;
      setCache(imgUrl, data.image);
    }
  }

  // Watch for URL changes and fetch
  watch(url, () => fetchImage(), { immediate: true });

  /** Call after uploading a new image to force a re-fetch (URL may not have changed). */
  function refresh() {
    fetchImage(true);
  }

  return { imageSrc, isLoading, isError, refresh };
}
